import React, { useMemo, useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label } from 'reactstrap'
import Select from 'react-select'
import { useQuery } from '@apollo/client'
import { DriverTypeAhead, RepTypeAhead } from '../TaskManagement/TaskComponents/TypeAheadComponents'
import { AllInboxesQuery } from './Queries'

import './CommsNewConversationModal.css'

const statusOptions = [
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Archived' },
]

const CommsEditConversationModal = ({
  isOpen,
  toggle,
  conversation,
  conversationMembers,
  assignableMembers,
  onSave,
}) => {
  const { data: allInboxesData } = useQuery(AllInboxesQuery)
  const driverName = conversation?.driver?.name
  const driverId = conversation?.driver?.id
  const defaultDriverLabelValue = driverName && driverId ? { value: driverId, label: driverName } : null
  const initialState = useMemo(() => ({
    subject: conversation?.subject || '',
    contactName: conversation?.contact_name || conversation?.contactName || '',
    status: conversation?.status || 'OPEN',
    driverId: conversation?.driver?.id || '',
    inboxIds: conversation?.inboxes?.edges?.map(e => e?.node?.id).filter(Boolean) || [],
    subscriberIds: conversation?.subscribers?.edges?.map(e => e?.node?.id).filter(Boolean) || [],
    assignedToId: conversation?.assignedTo?.id || '',
  }), [conversation])

  const [form, setForm] = useState(initialState)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => setForm(initialState), [initialState])
  useEffect(() => {
    if (isOpen) {
      setError('')
      setSaving(false)
    }
  }, [isOpen])

  const inboxOptions = useMemo(() => {
    const inboxes = allInboxesData?.allInboxes || []

    const normalized = Array.isArray(inboxes)
      ? inboxes
      : (inboxes.edges || []).map(edge => edge?.node).filter(Boolean)

    const fallback = (conversation?.channel?.inbox ? [conversation.channel.inbox] : [])
      .concat(conversation?.inboxes?.edges?.map(e => e?.node).filter(Boolean) || [])

    const merged = [...normalized, ...fallback]
    const deduped = []
    merged.forEach(inbox => {
      if (!inbox) return
      if (!deduped.some(i => i.id === inbox.id)) deduped.push(inbox)
    })

    return deduped.map(inbox => ({ value: inbox.id, label: inbox.name }))
  }, [conversation, allInboxesData])

  const subscriberOptions = useMemo(() => (
    (conversationMembers || []).map(member => ({ value: member.user.id, label: `${member.user.firstName} ${member.user.lastName}`.trim() || member.user.username }))
  ), [conversationMembers])

  const assigneeUsers = useMemo(() => {
    const seen = new Set()
    return [conversation?.assignedTo, ...(assignableMembers || []).map(member => member.user)]
      .filter((user) => {
        if (!user?.id || seen.has(user.id)) return false
        seen.add(user.id)
        return true
      })
  }, [assignableMembers, conversation?.assignedTo])

  const handleSave = async () => {
    setError('')
    setSaving(true)
    const input = {
      subject: form.subject || null,
      contactName: form.contactName || null,
      status: form.status,
      driverId: form.driverId || null,
      inboxIds: form.inboxIds,
      subscriberIds: form.subscriberIds,
      assignedToId: form.assignedToId || null,
    }
    try {
      const result = await onSave(input)
      const payload = result?.data?.messagingUpdateConversation
      const ok = payload?.ok
      const errors = payload?.errors || result?.errors

      if (ok === false || (Array.isArray(errors) && errors.length)) {
        const message = Array.isArray(errors) && errors.length
          ? errors.join(', ')
          : 'Failed to save changes'
        setError(message)
        return
      }
    } catch (err) {
      setError(err?.message || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md" className="CommsNewConversationModal">
      <ModalHeader toggle={toggle}>Edit Conversation</ModalHeader>
      <ModalBody>
        {error ? (
          <div className="alert alert-danger">Error: {error}</div>
        ) : null}

        <FormGroup>
          <Label>Inboxes</Label>
          <Select
            isMulti
            options={inboxOptions}
            value={inboxOptions.filter(o => form.inboxIds.includes(o.value))}
            onChange={(opts) => setForm({ ...form, inboxIds: opts ? opts.map(o => o.value) : [] })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Driver</Label>
          <DriverTypeAhead
            onChange={(driverId) => setForm({ ...form, driverId })}
            defaultLabelValue={defaultDriverLabelValue}
          />
        </FormGroup>

        <FormGroup>
          <Label for="status">Status</Label>
          <Select
            id="status"
            options={statusOptions}
            value={statusOptions.find(opt => opt.value === form.status) || statusOptions[0]}
            onChange={opt => setForm({ ...form, status: opt?.value })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Subscribers</Label>
          <Select
            isMulti
            options={subscriberOptions}
            value={subscriberOptions.filter(o => form.subscriberIds.includes(o.value))}
            onChange={(opts) => setForm({ ...form, subscriberIds: opts ? opts.map(o => o.value) : [] })}
          />
        </FormGroup>

        <FormGroup>
          <Label>Assigned To</Label>
          <RepTypeAhead
            reps={assigneeUsers}
            value={form.assignedToId}
            onChange={(opt) => setForm({ ...form, assignedToId: opt ? opt.value : '' })}
            isClearable
            placeholder="Assign conversation…"
            noOptionsMessage={() => 'No inbox members found'}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>Cancel</Button>
        <Button color="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default CommsEditConversationModal
